import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import styles from './Modal.module.scss';
import Button from '../Button/Button';

function Modal({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  footer, 
  size = 'medium', // 'small', 'medium', 'large'
  closeOnEsc = true,
  closeOnOutsideClick = true
}) {
  const modalRef = useRef(null);

  // Handle ESC key press to close modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (closeOnEsc && e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose, closeOnEsc]);

  // Handle outside click to close modal
  const handleOutsideClick = (e) => {
    if (closeOnOutsideClick && modalRef.current && !modalRef.current.contains(e.target)) {
      onClose();
    }
  };

  // Lock scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className={styles.modalOverlay} onClick={handleOutsideClick}>
      <div 
        className={`${styles.modalContent} ${styles[`size-${size}`]}`} 
        ref={modalRef}
      >
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>{title}</h2>
          <button 
            className={styles.closeButton} 
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className={styles.modalBody}>
          {children}
        </div>
        {footer && <div>{footer}</div>}
      </div>
    </div>,
    document.body
  );
}

// Modal.Footer component for convenience
Modal.Footer = ({ children }) => {
  return (
    <div className={styles.modalFooter}>
      {children}
    </div>
  );
};

// Modal.Actions component for standard action buttons
Modal.Actions = ({ onCancel, onConfirm, cancelText = 'Cancel', confirmText = 'Confirm' }) => {
  return (
    <Modal.Footer>
      <div className={styles.modalActions}>
        {onCancel && (
          <Button type="secondary" onClick={onCancel}>
            {cancelText}
          </Button>
        )}
        {onConfirm && (
          <Button type="primary" onClick={onConfirm}>
            {confirmText}
          </Button>
        )}
      </div>
    </Modal.Footer>
  );
};

export default Modal; 